import { useCallback, useEffect, useRef, useState } from "react";
import {
  AVATAR_COLORS,
  BALL_GRAVITY,
  BALL_RADIUS,
  COURT,
  GROUND_Y,
  NET_HALF_WIDTH,
  NET_TOP_Y,
  NET_X,
  PLAYER_HEIGHT,
  PLAYER_WIDTH,
  POINTS_TO_WIN,
  VOLLEY_PHASES,
  VOLLEY_STANCES,
  VolleyMsg,
  ballInReach,
  hitCenter,
  type VolleyAction,
  type VolleyActionPayload,
  type VolleyEndedPayload,
  type VolleyMovePayload,
  type VolleyPlayer,
  type VolleyState,
} from "@clubap/shared";
import type { Room } from "colyseus.js";
import { authedJson } from "../api.js";
import { VOLLEY_COURT_ART } from "../art/manifest.js";
import { createVolleyPractice, joinVolley } from "../net.js";

interface Props {
  token: string;
  /** El saldo nuevo tras el partido, para que el HUD no mienta. */
  onBalance: (balance: number) => void;
  onClose: () => void;
}

/** Lo que la UI necesita saber y cambia POCAS veces por segundo. */
interface Readout {
  phase: string;
  scoreLeft: number;
  scoreRight: number;
  nameLeft: string;
  nameRight: string;
  mySide: number | null;
  /** Para saber si el ganador que anuncia el servidor eres tú. */
  myAccountId: string;
  /** Decide si el botón dice GOLPEAR o REMATAR. */
  airborne: boolean;
  /** El balón está a tu alcance: la pista de "ya". */
  inReach: boolean;
  seconds: number;
}

const EMPTY: Readout = {
  phase: "esperando",
  scoreLeft: 0,
  scoreRight: 0,
  nameLeft: "",
  nameRight: "",
  mySide: null,
  myAccountId: "",
  airborne: false,
  inReach: false,
  seconds: 0,
};

/** Cuánto se extrapola como mucho entre parches, en segundos. */
const MAX_EXTRAPOLATION = 0.12;

const css = (color: number) => `#${color.toString(16).padStart(6, "0")}`;

/**
 * Abre la cancha, reintentando si el asiento anterior todavía figura ocupado.
 *
 * El servidor rechaza que una cuenta ocupe los dos lados, y esa comprobación mira las
 * conexiones vivas de la sala. Una que acaba de cerrarse sigue contando un instante, así
 * que cerrar el panel y volver a abrirlo de inmediato —o recargar la página a mitad de un
 * partido— se topaba con un rechazo definitivo por algo que se resuelve solo en medio
 * segundo. Reintentar unas pocas veces convierte ese caso en una espera imperceptible.
 *
 * Sólo se reintenta ESE rechazo. Un token inválido o una sala llena no mejoran esperando,
 * y reintentarlos sólo retrasaría el mensaje de error.
 */
async function connect(practice: boolean, token: string): Promise<Room<VolleyState>> {
  const attempts = 4;

  for (let attempt = 1; ; attempt++) {
    try {
      return practice ? await createVolleyPractice({ token }) : await joinVolley({ token });
    } catch (cause) {
      const busy = String((cause as Error)?.message ?? cause).includes("already_in_match");
      if (!busy || attempt >= attempts) throw cause;

      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  }
}

/**
 * El partido de volley.
 *
 * **Nada que se mueva vive en el estado de React.** El balón viaja a 30 parches por
 * segundo y los avatares con él: meterlos en `useState` sería un render por parche, que
 * es exactamente el error que en este repo ya costó 97 reconexiones. El bucle del canvas
 * lee `room.state` directamente en cada fotograma, y a React sólo suben las cosas que
 * cambian pocas veces —marcador, fase, si estás en el aire— y sólo cuando cambian de
 * verdad, comparando una firma.
 *
 * La cancha ya tiene su arte (`VOLLEY_COURT_ART`); los jugadores siguen siendo primitivas
 * de canvas a la espera del suyo. Lo que no cambia con el arte es de dónde saca los datos
 * — el estado sincronizado del servidor, que es el único que sabe dónde está el balón.
 */
export function VolleyGame({ token, onBalance, onClose }: Props) {
  const [readout, setReadout] = useState<Readout>(EMPTY);
  const [result, setResult] = useState<VolleyEndedPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * Pedir rival de computadora es un MODO, no un botón que dispara algo suelto.
   *
   * Está en el estado y entra en las dependencias del efecto de conexión: al activarlo, la
   * limpieza deja la cancha en la que se esperaba y se crea una de práctica. Manejarlo a
   * mano —salir aquí, entrar allá— es como se acaba con dos salas abiertas a la vez.
   */
  const [practice, setPractice] = useState(false);

  const roomRef = useRef<Room<VolleyState> | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** La última dirección enviada. Sin esto se mandaría un mensaje por fotograma. */
  const dirRef = useRef(0);
  /** Cuándo llegó el último parche, para extrapolar el vuelo del balón. */
  const patchAtRef = useRef(performance.now());

  /*
   * Las devoluciones en refs para que el efecto de conexión NO dependa de ellas.
   *
   * Es la lección del bug de las 97 reconexiones, aplicada antes de que ocurra: si el
   * efecto dependiera de `onBalance`, cada render del padre lo volvería a ejecutar, y su
   * limpieza deja la cancha — el partido se caería solo a mitad.
   */
  const balanceRef = useRef(onBalance);
  balanceRef.current = onBalance;

  /*
   * `onClose` también en una ref, y por un motivo distinto y más sutil.
   *
   * Llega como función nueva en cada render del padre —el HUD se redibuja con cada moneda
   * y cada jugador que entra— así que tenerla en las dependencias del efecto de teclado lo
   * resuscribiría constantemente. Y ese efecto guarda QUÉ TECLAS ESTÁN PULSADAS: al
   * resuscribirse, ese conjunto se vacía, el `keyup` de la tecla que sostienes ya no
   * encuentra nada que borrar, y el jugador se queda caminando hacia un lado para siempre.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  /**
   * Toda la sesión de la cancha en UN efecto, que depende sólo del token.
   *
   * El token es una cadena estable; el resto entra por refs. Así entrar al partido, jugarlo
   * y salir es un solo ciclo de vida y no hay forma de que un render intermedio lo parta.
   */
  useEffect(() => {
    let cancelled = false;
    let room: Room<VolleyState> | null = null;

    void (async () => {
      try {
        /*
         * Un respiro ANTES de abrir el socket, y no es una tirita: es lo que hace correcto
         * a este efecto bajo StrictMode.
         *
         * En desarrollo React monta, desmata y vuelve a montar el componente de un tirón.
         * Sin esta pausa eso abría DOS conexiones a la cancha, y la segunda chocaba contra
         * la regla de que una cuenta no puede ocupar los dos asientos: el servidor la
         * rechazaba, la primera ya venía cancelada y se iba, y el panel se quedaba sin sala
         * — la cancha se dibujaba vacía y no había forma de jugar.
         *
         * Con la pausa, el montaje descartado se entera de que lo cancelaron antes de
         * llegar a conectarse, y sólo el bueno abre socket.
         */
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (cancelled) return;

        const joined = await connect(practice, token);

        if (cancelled) {
          await joined.leave();
          return;
        }

        room = joined;
        roomRef.current = joined;

        joined.onStateChange(() => {
          patchAtRef.current = performance.now();
        });

        joined.onMessage<VolleyEndedPayload>(VolleyMsg.Ended, (payload) => {
          if (cancelled) return;
          setResult(payload);

          /*
           * El saldo se RELEE de la base en vez de sumarle las monedas del aviso.
           *
           * El aviso dice cuánto se ganó en este partido; el saldo real pudo cambiar por
           * otra pestaña, y sumar a ciegas dejaría el HUD contando una cifra propia que
           * se aleja de la verdad. Releer cuesta una petición y no puede desviarse.
           *
           * Se usa `authedJson` y NO `resumeSession`, que hace lo mismo pero **descarta el
           * token** si la petición falla. Eso está bien al arrancar la app, donde un token
           * muerto significa volver al ingreso; acá significaría que un tropiezo de red al
           * cerrar un partido te saca de la sesión.
           */
          void authedJson<{ player: { coins: number } }>("/api/me")
            .then((body) => {
              if (!cancelled) balanceRef.current(body.player.coins);
            })
            .catch(() => {
              /* El marcador ya se mostró; un saldo viejo en el HUD no rompe nada. */
            });
        });

        joined.onError((code, message) => {
          if (!cancelled) setError(message ?? `error ${code}`);
        });
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "No se pudo entrar a la cancha.");
        }
      }
    })();

    return () => {
      cancelled = true;
      roomRef.current = null;
      void room?.leave().catch(() => {});
    };
    // `practice` entra en las dependencias: cambiar de modo cierra la cancha anterior por
    // la limpieza de arriba, en vez de dejarla abierta mientras se abre la siguiente.
  }, [token, practice]);

  /** Manda la dirección sólo cuando CAMBIA. */
  const steer = useCallback((dir: number) => {
    if (dirRef.current === dir) return;
    dirRef.current = dir;
    roomRef.current?.send(VolleyMsg.Move, { dir } satisfies VolleyMovePayload);
  }, []);

  const act = useCallback((action: VolleyAction) => {
    roomRef.current?.send(VolleyMsg.Action, { action } satisfies VolleyActionPayload);
  }, []);

  /* Teclado, además de los botones: jugar esto con el ratón es incómodo. */
  useEffect(() => {
    const held = new Set<string>();

    const applySteer = () => {
      const left = held.has("ArrowLeft") || held.has("a");
      const right = held.has("ArrowRight") || held.has("d");
      steer(left === right ? 0 : left ? -1 : 1);
    };

    const down = (event: KeyboardEvent) => {
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;

      if (key === "Escape") {
        closeRef.current();
        return;
      }

      if (["ArrowLeft", "ArrowRight", "a", "d"].includes(key)) {
        held.add(key);
        applySteer();
        event.preventDefault();
        return;
      }

      // `repeat` fuera: mantener la tecla no debe disparar una acción por repetición.
      if (event.repeat) return;

      if (key === " " || key === "ArrowUp" || key === "w") {
        act("saltar");
        event.preventDefault();
      } else if (key === "s") {
        act("planchar");
      } else if (key === "j") {
        act("golpear");
      } else if (key === "k") {
        act("bloquear");
      } else if (key === "l") {
        act("recibir");
      }
    };

    const up = (event: KeyboardEvent) => {
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      if (held.delete(key)) applySteer();
    };

    /* Soltar el foco de la ventana cuenta como soltar todas las teclas. */
    const blur = () => {
      held.clear();
      applySteer();
    };

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);

    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
    // Las dos dependencias son estables (useCallback sin dependencias) y el cierre entra
    // por ref: el efecto se suscribe UNA sola vez, así que el conjunto de teclas pulsadas
    // sobrevive a cualquier render del padre.
  }, [act, steer]);

  /** El bucle de dibujo. Lee el estado del servidor; no toca React salvo por la firma. */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    let frame = 0;
    let signature = "";

    /*
     * El fondo se carga aquí y se pinta cuando esté listo. Mientras tanto —o si el archivo
     * falta— se dibuja la cancha con primitivas, que es la regla de todo el arte del
     * juego: una imagen rota nunca deja la pantalla sin nada que mostrar.
     */
    let background: HTMLImageElement | null = null;
    if (VOLLEY_COURT_ART) {
      background = new Image();
      background.src = VOLLEY_COURT_ART;
      background.onerror = () => {
        console.warn(`[art] no se pudo cargar ${VOLLEY_COURT_ART}; la cancha usa primitivas`);
        background = null;
      };
    }

    const draw = () => {
      frame = requestAnimationFrame(draw);

      const room = roomRef.current;
      const state = room?.state;

      const art = background?.complete && background.naturalWidth > 0 ? background : null;
      paintCourt(context, art);
      if (!room || !state || state.players.size === 0) return;

      /*
       * Se extrapola el vuelo con la velocidad que el servidor sincroniza.
       *
       * Entre dos parches el balón recorre hasta 23 unidades, así que dibujarlo donde
       * llegó el último lo dejaría a tirones justo en el remate, que es cuando importa.
       * La gravedad se conoce, así que la curva se reconstruye exacta en vez de adivinarla.
       */
      const dt = Math.min((performance.now() - patchAtRef.current) / 1000, MAX_EXTRAPOLATION);
      const ballX = state.ballX + state.ballVx * dt;
      const ballY = state.ballY + state.ballVy * dt + 0.5 * BALL_GRAVITY * dt * dt;

      let mine: VolleyPlayer | undefined;
      const sides: Array<VolleyPlayer | undefined> = [undefined, undefined];

      state.players.forEach((player, sessionId) => {
        sides[player.side === 0 ? 0 : 1] = player;
        if (sessionId === room.sessionId) mine = player;
        paintPlayer(context, player);
      });

      paintBall(context, ballX, ballY);

      const airborne = mine ? mine.y < GROUND_Y : false;
      const inReach = mine ? ballInReach({ x: ballX, y: ballY }, mine.x, mine.y) : false;

      if (mine && inReach) paintReachHint(context, mine);

      const next: Readout = {
        phase: VOLLEY_PHASES[state.phase] ?? "esperando",
        scoreLeft: sides[0]?.score ?? 0,
        scoreRight: sides[1]?.score ?? 0,
        nameLeft: sides[0]?.nickname ?? "—",
        nameRight: sides[1]?.nickname ?? "—",
        mySide: mine ? mine.side : null,
        myAccountId: mine?.accountId ?? "",
        airborne,
        inReach,
        seconds: Math.ceil(state.remainingMs / 1000),
      };

      /*
       * La firma es lo que impide un render por fotograma.
       *
       * Es el mismo truco que usa la lista de jugadores del campus: si nada de lo que la
       * UI MUESTRA cambió, no se toca el estado de React aunque el balón lleve mil
       * píxeles recorridos.
       */
      const stamp = Object.values(next).join("|");
      if (stamp !== signature) {
        signature = stamp;
        setReadout(next);
      }
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  const waiting = readout.phase === "esperando";
  const over = result !== null;

  return (
    <div className="overlay">
      <div className="panel volley">
        <div className="game__head">
          <h2 className="game__title">Volley del coliseo</h2>
          {!waiting && !over && <span className="game__timer">{readout.seconds}s</span>}
          <span className="game__score">
            {readout.scoreLeft} — {readout.scoreRight}
          </span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div className="volley__names">
          <span className={readout.mySide === 0 ? "volley__name volley__name--mine" : "volley__name"}>
            {readout.nameLeft}
          </span>
          <span className="volley__target">a {POINTS_TO_WIN}, ventaja de 2</span>
          <span className={readout.mySide === 1 ? "volley__name volley__name--mine" : "volley__name"}>
            {readout.nameRight}
          </span>
        </div>

        <div className="volley__court">
          <canvas ref={canvasRef} width={COURT.width} height={COURT.height} className="volley__canvas" />

          {waiting && !error && (
            <div className="game__center">
              <p className="game__hint">
                {practice ? "Preparando la cancha…" : "Esperando rival…"}
              </p>

              {!practice && (
                <>
                  <button className="btn" type="button" onClick={() => setPractice(true)}>
                    Jugar contra la computadora
                  </button>
                  <p className="game__hint volley__aside">
                    El partido empieza cuando otra persona toque el balón del coliseo. Tiene
                    que ser otra cuenta: nadie ocupa los dos lados.
                  </p>
                  {/* Se avisa ANTES de jugar, no al final: descubrir que no pagaba después
                      de ganar un partido a 11 se siente como un engaño. */}
                  <p className="game__hint volley__aside">
                    Los partidos de práctica no dan monedas.
                  </p>
                </>
              )}
            </div>
          )}

          {over && (
            <div className="game__center">
              <p className="game__result">
                {result.reason === "abandono"
                  ? "Tu rival abandonó"
                  : result.reason === "tiempo"
                    ? "Se acabó el tiempo"
                    : "Partido terminado"}
              </p>
              <p className="game__hint">
                {result.scoreA} — {result.scoreB}
              </p>
              <p className="game__hint">
                {result.winnerAccountId === null
                  ? "Empate"
                  : result.winnerAccountId === readout.myAccountId
                    ? "Ganaste"
                    : "Perdiste"}
              </p>
              {result.coinsAwarded > 0 ? (
                <p className="game__result">+{result.coinsAwarded} monedas</p>
              ) : (
                <p className="game__hint">
                  {result.cappedByLimit
                    ? "Alcanzaste el techo de monedas por hora."
                    : "Sin monedas por este partido."}
                </p>
              )}
              <button className="btn" type="button" onClick={onClose}>
                Cerrar
              </button>
            </div>
          )}
        </div>

        {/*
          La guía es TODA la interfaz de control: los botones en pantalla se quitaron
          porque jugar esto con el ratón —moverse, saltar y golpear a la vez— no funciona.
        */}
        <p className="game__hint volley__keys">
          <strong>A</strong> izquierda · <strong>D</strong> derecha · <strong>Espacio</strong>{" "}
          saltar ·{" "}
          {/* La MISMA tecla cambia de nombre en el aire. Es lo que antes decía el botón:
              el mensaje que sale es el mismo, y quien decide si eso es una colocada o un
              remate es el servidor mirando si estás en el aire. */}
          <strong className={readout.airborne ? "volley__key--hot" : undefined}>J</strong>{" "}
          <span className={readout.airborne ? "volley__key--hot" : undefined}>
            {readout.airborne ? "¡rematar!" : "golpear"}
          </span>{" "}
          · <strong>K</strong> bloquear · <strong>L</strong> recibir ·{" "}
          <strong>S</strong> planchar
        </p>

        {error && (
          <p className="game__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function paintCourt(context: CanvasRenderingContext2D, art: HTMLImageElement | null): void {
  context.clearRect(0, 0, COURT.width, COURT.height);

  if (art) {
    context.drawImage(art, 0, 0, COURT.width, COURT.height);
  } else {
    const sky = context.createLinearGradient(0, 0, 0, GROUND_Y);
    sky.addColorStop(0, "#123049");
    sky.addColorStop(1, "#1d4a6b");
    context.fillStyle = sky;
    context.fillRect(0, 0, COURT.width, GROUND_Y);

    context.fillStyle = "#c8823c";
    context.fillRect(0, GROUND_Y, COURT.width, COURT.height - GROUND_Y);

    context.strokeStyle = "#ffffff55";
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(0, GROUND_Y);
    context.lineTo(COURT.width, GROUND_Y);
    context.stroke();
  }

  /*
   * La red se dibuja SIEMPRE, también sobre el arte, y no es redundancia.
   *
   * El fondo trae el poste, pero la altura que hay que superar la decide `NET_HEIGHT`, y
   * es el dato que más importa en la pantalla: con sólo el dibujo, quien juega calcularía
   * contra la punta del poste y no contra la red de verdad. Sobre la arena amarilla la
   * malla va oscura y la cinta blanca, que es como se ve una red de playa.
   */
  context.fillStyle = art ? "#1b1b1bcc" : "#e8eef5";
  context.fillRect(NET_X - NET_HALF_WIDTH, NET_TOP_Y, NET_HALF_WIDTH * 2, GROUND_Y - NET_TOP_Y);
  context.fillStyle = art ? "#ffffff" : "#ffd75e";
  context.fillRect(NET_X - NET_HALF_WIDTH - 2, NET_TOP_Y - 4, NET_HALF_WIDTH * 2 + 4, 5);

  if (art) {
    context.strokeStyle = "#1b1b1b";
    context.lineWidth = 1.5;
    context.strokeRect(NET_X - NET_HALF_WIDTH - 2, NET_TOP_Y - 4, NET_HALF_WIDTH * 2 + 4, 5);
  }
}

function paintPlayer(context: CanvasRenderingContext2D, player: VolleyPlayer): void {
  const stance = VOLLEY_STANCES[player.stance] ?? "reposo";
  const color = css(AVATAR_COLORS[player.color] ?? AVATAR_COLORS[0]);
  const top = player.y - PLAYER_HEIGHT;

  // Sombra en el suelo: sin ella no se distingue un salto de estar más arriba.
  context.fillStyle = "#00000033";
  context.beginPath();
  context.ellipse(player.x, GROUND_Y + 3, PLAYER_WIDTH / 2, 5, 0, 0, Math.PI * 2);
  context.fill();

  /*
   * Planchando el cuerpo se dibuja TUMBADO, no de pie.
   *
   * No es adorno: es la única señal de que estás en el suelo y de por qué durante ese
   * momento las teclas no responden. Sin ella, la recuperación se lee como un cuelgue.
   */
  const prone = stance === "plancha" || stance === "levantandose";
  const bodyW = prone ? PLAYER_HEIGHT : PLAYER_WIDTH;
  const bodyH = prone ? PLAYER_WIDTH : PLAYER_HEIGHT;
  const bodyTop = player.y - bodyH;

  context.fillStyle = color;
  context.fillRect(player.x - bodyW / 2, bodyTop, bodyW, bodyH);

  context.fillStyle = "#f5d9b8";
  context.beginPath();
  context.arc(prone ? player.x + bodyW / 2 : player.x, prone ? bodyTop + bodyH / 2 : top - 6, 9, 0, Math.PI * 2);
  context.fill();

  /*
   * Los brazos DICEN la postura, que es lo único que el jugador necesita leer mientras
   * el arte lateral no llega: arriba es bloqueo, abajo y juntos es recepción, atrás es
   * remate. Es información de juego, no decoración.
   */
  const hands = hitCenter(player.x, player.y);
  context.strokeStyle = stance === "bloqueo" ? "#ffd75e" : "#f5d9b8";
  context.lineWidth = 6;
  context.lineCap = "round";
  context.beginPath();

  if (stance === "plancha" || stance === "levantandose") {
    // Un brazo estirado por delante: es lo que explica de un vistazo por qué llega tan
    // lejos y por qué durante un momento no responde.
    context.moveTo(player.x, player.y - 10);
    context.lineTo(player.x + (stance === "plancha" ? 30 : 16), player.y - 4);
  } else if (stance === "bloqueo") {
    context.moveTo(player.x - 10, top + 6);
    context.lineTo(player.x - 12, top - 20);
    context.moveTo(player.x + 10, top + 6);
    context.lineTo(player.x + 12, top - 20);
  } else if (stance === "recepcion") {
    context.moveTo(player.x - 6, hands.y);
    context.lineTo(player.x, player.y - 12);
    context.moveTo(player.x + 6, hands.y);
    context.lineTo(player.x, player.y - 12);
  } else if (stance === "remate" || stance === "golpe") {
    context.moveTo(player.x, top + 8);
    context.lineTo(player.x + 22, top - 10);
  } else {
    context.moveTo(player.x - 12, top + 10);
    context.lineTo(player.x - 14, hands.y + 8);
    context.moveTo(player.x + 12, top + 10);
    context.lineTo(player.x + 14, hands.y + 8);
  }

  context.stroke();

  context.fillStyle = "#ffffffcc";
  context.font = "11px system-ui, sans-serif";
  context.textAlign = "center";
  context.fillText(player.nickname, player.x, bodyTop - 14);
}

function paintBall(context: CanvasRenderingContext2D, x: number, y: number): void {
  context.fillStyle = "#ffe9a8";
  context.strokeStyle = "#8f6410";
  context.lineWidth = 2;
  context.beginPath();
  context.arc(x, y, BALL_RADIUS, 0, Math.PI * 2);
  context.fill();
  context.stroke();
}

/** El aro de "ya": el balón está a tu alcance. Mismo criterio que usa el servidor. */
function paintReachHint(context: CanvasRenderingContext2D, player: VolleyPlayer): void {
  const hands = hitCenter(player.x, player.y);

  context.strokeStyle = "#ffd75e99";
  context.lineWidth = 2;
  context.setLineDash([4, 4]);
  context.beginPath();
  context.arc(hands.x, hands.y, 34, 0, Math.PI * 2);
  context.stroke();
  context.setLineDash([]);
}
